import React from "react";
import { Box, Typography, Grid, Paper, Alert, Stack } from "@mui/material";
import { APISession } from "../../../redux/reducers/scheduleReducer";
import NearlySchedulableSessionCard from "./NearlySchedulableSessionCard";
import EventBusyIcon from "@mui/icons-material/EventBusy";

interface Props {
  sessions: APISession[];
  selectedSessionId: string | null;
  onSelectSession: (session: APISession) => void;
}

const NearlySchedulableSessionsList: React.FC<Props> = ({
  sessions,
  selectedSessionId,
  onSelectSession,
}) => {
  if (sessions.length === 0) {
    return (
      <Box sx={{ pt: 3, pb: 3 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "#f5f5f5",
            border: "1px dashed #ccc",
          }}
        >
          <EventBusyIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Nearly Schedulable Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All sessions are either scheduled or unschedulable.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Group sessions by status
  const sessionsByStatus = sessions.reduce((acc, session) => {
    const status = session.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(session);
    return acc;
  }, {} as Record<string, APISession[]>);

  return (
    <Box
      sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 } }}
    >
      <Stack spacing={3}>
        {/* Summary Alert */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{sessions.length}</strong> session
            {sessions.length !== 1 ? "s" : ""} nearly schedulable
          </Typography>
        </Alert>

        {/* Sessions grouped by status */}
        {Object.entries(sessionsByStatus).map(([status, statusSessions]) => (
          <Box key={status}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: "#2d3748",
                textTransform: "capitalize",
              }}
            >
              {status.replace(/_/g, " ")} ({statusSessions.length})
            </Typography>
            <Grid container spacing={2}>
              {statusSessions.map((session) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={session.session_id}
                >
                  <NearlySchedulableSessionCard
                    session={session}
                    onClick={() => onSelectSession(session)}
                    isSelected={session.session_id === selectedSessionId}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default NearlySchedulableSessionsList;
